import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";

import { PageHeader } from "@/components/dashboard/page-header";
import { TrackingBoard } from "@/components/tracking/tracking-board";
import { nav, tracking as copy } from "@/content";
import { readNumber } from "@/utils/query-params";
import { getRoster, getTrail } from "@/api/attendance";
import { listZoneOptionsWithGeometry } from "@/api/zones";
import { instantAt, startOfLocalDay, toDateKey } from "@/utils/time";
import { useQuery } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useSession } from "@/context/UserContext";

/**
 * Live tracking.
 *
 * Rendered on the server on every request and refreshed by the client, rather
 * than assembled in the browser from a socket feed. Duty state, staleness and
 * "which zone is this position in" are all decisions with rules attached, and the
 * rules live on the server — the prototype derived them client-side from
 * whichever socket message happened to arrive, which is how it ended up
 * disagreeing with itself about who was on duty.
 *
 * Supervisors see their own team. The prototype scoped its tracking endpoint the
 * same way and then gave supervisors no zone access at all, so the board could
 * tell them a guard was "outside any zone" with no way to see where the zone was.
 * Zone polygons are drawn here for every role that can read the board.
 */
export const Route = createFileRoute("/_main/dashboard/tracking")({
  component: () => (
    <RequirePermission action="tracking.read">
      <TrackingPage />
    </RequirePermission>
  ),
});

function TrackingPage() {
  const { user, settings } = useSession();
  useDocumentTitle(copy.title);
  const params = useQueryParams();
  const focusedGuardId = readNumber(params, "guard") ?? null;
  const wantsTrail = params.trail === "1" && focusedGuardId !== null;

  /* The roster is the one read in the console that is genuinely live. The SSE
     stream tells the board when to re-read; this interval is the floor under it,
     so a dropped stream degrades to polling rather than to a frozen board — which
     is exactly what the prototype did, fetching once on mount and then relying on
     a socket that was null by the time it looked. */
  const rosterQuery = useQuery({
    queryKey: ["tracking", "roster"],
    queryFn: getRoster,
    refetchInterval: 30_000,
    staleTime: 0,
  });

  const zonesQuery = useQuery({
    queryKey: ["tracking", "zoneGeometry"],
    queryFn: listZoneOptionsWithGeometry,
    staleTime: 5 * 60_000,
  });

  const trailQuery = useQuery({
    queryKey: ["tracking", "trail", focusedGuardId],
    queryFn: () =>
      getTrail({
        guardId: focusedGuardId as number,
        /* Today, in the operating timezone. The read the prototype's `locations`
           table never had: every ping stored and only ever queried one row at a
           time. */
        from: startOfLocalDay(new Date(), settings.timeZone),
        to: instantAt(toDateKey(new Date(), settings.timeZone), "23:59", settings.timeZone),
      }),
    enabled: wantsTrail && focusedGuardId !== null,
  });

  const roster = rosterQuery.data ?? [];
  const zones = zonesQuery.data ?? [];
  const trail = trailQuery.data ?? [];

  /* A guard the caller cannot see must not be focusable by URL. */
  const focused = roster.some((entry) => entry.guardId === focusedGuardId)
    ? focusedGuardId
    : null;

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader eyebrow={nav.groups.monitoring} title={copy.title} lead={copy.lead} />

      <TrackingBoard
        /* Dates are serialised across the client boundary explicitly rather than
           relying on the RSC payload's Date support, so the client renders them
           through `fmt` with the organisation's timezone and the user's numerals. */
        roster={roster.map((entry) => ({
          ...entry,
          recordedAt: entry.recordedAt?.toISOString() ?? null,
          lastPunchAt: entry.lastPunchAt?.toISOString() ?? null,
        }))}
        zones={zones.map((zone) => ({
          id: zone.id,
          name: zone.name,
          color: zone.color,
          polygon: zone.polygon,
        }))}
        centre={{ lat: settings.defaultMapLat, lng: settings.defaultMapLng }}
        numerals={user.numeralSystem}
        timeZone={settings.timeZone}
        staleMinutes={settings.staleSignalMinutes}
        trail={trail.map((point) => ({
          lat: point.lat,
          lng: point.lng,
          recordedAt: point.recordedAt.toISOString(),
        }))}
        focusedGuardId={focused}
      />
    </div>
  );
}
